import { AnyAction, createAsyncThunk, ThunkDispatch } from "@reduxjs/toolkit"

import { GlobalState } from "../../store"
import { addErrorNotification } from "../notifications/actions"
import { resourcesAPI } from "../../apis"
import {
    IActionButton,
    IAttachment,
    ICollection,
    IConcept,
    IItem,
    IWorkspace,
} from "./index"

export enum ResourcePrefix {
    ActionButton = "actionButton",
    Attachment = "attachment",
    Collection = "collection",
    Concept = "concept",
    Item = "item",
    Workspace = "workspace",
}

export const prefixId = (type: ResourcePrefix, id: number | string): string =>
    `${type}.${id}`

export const defaultResourceFetchErrorMessage =
    "Something went wrong when fetching resource with id: "

const errorHandler = (
    error: Response,
    dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
    id: number | string,
): void => {
    console.error(error)
    // we expect a 404 when the resource doesn't exist
    if (error.status !== 404) {
        dispatch(
            addErrorNotification(`${defaultResourceFetchErrorMessage} ${id}.`),
        )
    }
}

const checkFetchCondition = (
    getState: () => unknown,
    type: ResourcePrefix,
    id: number | string,
): boolean => {
    const {
        resources: { loadingResources },
    } = getState() as GlobalState

    // only allow this action if the resource is not loading yet
    return !loadingResources.includes(prefixId(type, id))
}

export const fetchActionButton = createAsyncThunk(
    "resources/fetchActionButton",
    async (id: string, { dispatch }) => {
        let actionButton: IActionButton | null = null

        try {
            actionButton =
                await resourcesAPI.actionButtonApi.getActionButtonData(id)
        } catch (error) {
            errorHandler(error, dispatch, id)
        }

        if (!actionButton) {
            actionButton = {
                id,
                doesExist: false,
            }
        }

        return actionButton
    },
    {
        condition: (id: string, { getState }) => {
            return checkFetchCondition(
                getState,
                ResourcePrefix.ActionButton,
                id,
            )
        },
    },
)

export const fetchAttachment = createAsyncThunk(
    "resources/fetchAttachment",
    async (id: number, { dispatch }) => {
        let attachment: IAttachment | null = null

        try {
            attachment = await resourcesAPI.attachmentApi.getAttachment(id)
        } catch (error) {
            errorHandler(error, dispatch, id)
        }

        if (!attachment) {
            attachment = {
                id,
                filename: "",
                fileSize: "",
                doesExist: false,
            }
        }

        return attachment
    },
    {
        condition: (id: number, { getState }) => {
            return checkFetchCondition(getState, ResourcePrefix.Attachment, id)
        },
    },
)

export const fetchCollection = createAsyncThunk(
    "resources/fetchCollection",
    async (id: number, { dispatch }) => {
        let collection: ICollection | null = null

        try {
            collection = await resourcesAPI.collectionApi.getCollection(id)
        } catch (error) {
            errorHandler(error, dispatch, id)
        }

        if (!collection) {
            collection = {
                id,
                title: "",
                doesExist: false,
            }
        }

        return collection
    },
    {
        condition: (id: number, { getState }) => {
            return checkFetchCondition(getState, ResourcePrefix.Collection, id)
        },
    },
)

export const fetchConcept = createAsyncThunk(
    "resources/fetchConcept",
    async (id: number, { dispatch }) => {
        let concept: IConcept | null = null

        try {
            concept = await resourcesAPI.conceptApi.getConcept(id)
        } catch (error) {
            errorHandler(error, dispatch, id)
        }

        if (!concept) {
            concept = {
                id,
                title: "",
                doesExist: false,
            }
        }

        return concept
    },
    {
        condition: (id: number, { getState }) => {
            return checkFetchCondition(getState, ResourcePrefix.Concept, id)
        },
    },
)

export const fetchItem = createAsyncThunk(
    "resources/fetchItem",
    async (id: number, { dispatch }) => {
        let item: IItem | null = null

        try {
            item = await resourcesAPI.itemApi.getItem(id)
        } catch (error) {
            errorHandler(error, dispatch, id)
        }

        if (!item) {
            item = {
                id,
                title: "",
                doesExist: false,
            }
        }

        return item
    },
    {
        condition: (id: number, { getState }) => {
            return checkFetchCondition(getState, ResourcePrefix.Item, id)
        },
    },
)

export const fetchWorkspace = createAsyncThunk(
    "resources/fetchWorkspace",
    async (id: number, { dispatch }) => {
        let workspace: IWorkspace | null = null

        try {
            workspace = await resourcesAPI.workspaceApi.getWorkspace(id)
        } catch (error) {
            errorHandler(error, dispatch, id)
        }

        if (!workspace) {
            workspace = {
                id,
                title: "",
                doesExist: false,
            }
        }

        return workspace
    },
    {
        condition: (id: number, { getState }) => {
            return checkFetchCondition(getState, ResourcePrefix.Workspace, id)
        },
    },
)
